import { useState, useEffect, ChangeEvent } from 'react';
import { Card, Col, Row, Toast, ToastContainer } from 'react-bootstrap';

import { Link, useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import useAdvanceTable from 'hooks/useAdvanceTable';
import DatePicker from 'components/base/DatePicker';
import {
    faFileExport,
    faFilter,
    faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'providers/AuthProvider'
import apiClient, { handleAPIError } from 'helpers/apiService';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Button from 'components/base/Button';
import LeadsFilterModal from 'components/modals/LeadsFilterModal';


interface Resource {
    resource_id: number;

    resource_name: string;
    resource_type: string;
    meta: string;
    resource_uri: string;
    notes: string;
    inserted_at: string;
}

const ResourcesTable = () => {
    return (
        <div className="border-top">
            <AdvanceTable
                tableProps={{ className: 'phoenix-table fs-9' }}
                rowClassName="hover-actions-trigger btn-reveal-trigger"
            />
            <AdvanceTableFooter pagination className="py-4" />
        </div>
    );
};


const CoachingReports = () => {
    const { t } = useTranslation();

    const breadcrumbItems: PageBreadcrumbItem[] = [
        {
            label: t('Dashboard'),
            url: '/dashboard/home'
        },
        {
            label: t('Analytics.page_title'),
            url: '/analytics'
        },
        {
            label: t('CoachingReports.page_title'),
            active: true
        }
    ];


    const auth = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);

    const [openFilterModal, setOpenFilterModal] = useState(false);
    const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        table.setGlobalFilter(e.target.value || undefined);
    };

    const [resources, setResources] = useState<Resource[]>([]);

    // State to control Toast messages
    const [show, setShow] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    // already uploaded file (resource)
    const [fileUrl, setFileUrl] = useState('');
    const [fileName, setFileName] = useState('');
    //const [resourceId, setResourceId] = useState(0);


    const fetchCoachingReport = async (resource_id: number) => {
        try {
            const response = await apiClient.get(
                `/v1/resources/${resource_id}/download`,
                {
                    headers: {
                        'accept': 'application/json',

                    },
                    withCredentials: true,
                }
            );

            setFileUrl(response.data['resource_url']);
            setFileName(response.data['resource_name']);

            // Open the file URL in a new tab for preview
            if (response.data['resource_url']) {
                const newWindow = window.open(response.data['resource_url'], '_blank', 'noopener,noreferrer');
                if (!newWindow) {
                    console.error("Failed to open new window. Please check your browser settings.");
                }
            } else {
                console.error("File URL not found");
            }


        } catch (error) {
            console.error('Error fetching coaching report:', error);
        } finally {
            setLoading(false);
        }
    };

    const reportListTableColumns: ColumnDef<Resource>[] = [
        {
            header: t('Resource.table.title'),
            accessorKey: 'resource_anme',
            cell: ({ row: { original } }) => {
                const { resource_id, resource_name } = original;
                return (
                    <p onClick={() => fetchCoachingReport(resource_id)} className="link-primary fw-semi-bold" style={{ cursor: 'pointer' }}>
                        {resource_name}
                    </p>
                );
            },
            meta: {
                cellProps: { className: 'white-space-nowrap py-4' },
                headerProps: { style: { width: '25%' } }
            }
        },


        {
            header: t('Resource.table.type'),
            accessorKey: 'resource_type',
            meta: {
                headerProps: { style: { width: '10%' }, className: 'ps-3' }
            }
        },

        {
            header: 'Date',
            accessorKey: 'inserted_at',
            meta: {
                cellProps: { className: 'ps-3 text-900 py-4' },
                headerProps: { style: { width: '12%' }, className: 'ps-3' }
            }
        },


    ];

    const table = useAdvanceTable({
        data: resources,
        columns: reportListTableColumns,
        pageSize: 6,
        pagination: true,
        sortable: true
    });


    const fetchData = async () => {
        try {
            const response = await apiClient.get(
                '/v1/resources?type=FEEDBACK-REPORT',
                {
                    headers: {
                        'accept': 'application/json',

                    },
                    withCredentials: true,
                }
            );
            setResources(response.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);



    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            <div>
                <ToastContainer className="p-3" position='top-center'>
                    <Toast onClose={() => setShow(false)} show={show} delay={3000} autohide>
                        <Toast.Body className=''>{toastMessage}</Toast.Body>
                    </Toast>
                </ToastContainer>
            </div>

            <PageBreadcrumb items={breadcrumbItems} />

            <div className="mt-3  mb-3">

                <Card className='border border-light rounded-5' >
                    <Card.Body>
                        <AdvanceTableProvider {...table}>
                            <Row className="g-3 justify-content-between mb-4">
                                <Col xs="auto">

                                    <Button
                                        variant="link"
                                        className="text-900 px-0"
                                        startIcon={
                                            <FontAwesomeIcon icon={faFileExport} className="fs-9 me-2" />
                                        }
                                    >
                                        {t("CoachingReports.export")}
                                    </Button>
                                </Col>
                                <Col xs="auto">
                                    <div className="d-flex">
                                        <SearchBox
                                            placeholder="Search by name"
                                            className="me-2"
                                            onChange={handleSearchInputChange}
                                        />
                                        <DatePicker defaultValue="Mar 1, 2022" />
                                        <Button
                                            variant="phoenix-secondary"
                                            className="px-3 ms-2"
                                            onClick={() => setOpenFilterModal(true)}
                                        >
                                            <FontAwesomeIcon
                                                icon={faFilter}
                                                transform="down-3"
                                                className="text-primary"
                                            />
                                        </Button>
                                    </div>
                                    <LeadsFilterModal
                                        show={openFilterModal}
                                        handleClose={() => setOpenFilterModal(false)}
                                    />
                                </Col>
                            </Row>
                            <ResourcesTable />
                        </AdvanceTableProvider>
                    </Card.Body>
                </Card>
            </div>
        </div>

    );


};

export default CoachingReports;
